import { useEffect } from 'react';
import { useStoredChoice } from './storedChoice';

// Personal display choices, kept on this device (Gary, 18 Oct): how much of the last message a
// chat row shows, and light or dark. Applied as attributes on <html> so chats.css does the rest.

export const DENSITIES = ['compact', 'normal', 'expanded'] as const;
export type Density = (typeof DENSITIES)[number];
export const THEMES = ['light', 'dark', 'system'] as const;
export type Theme = (typeof THEMES)[number];

const DENSITY_KEY = 'chats.density';
const THEME_KEY = 'chats.theme';

/** The browser bar and phone status bar follow the theme too. */
function setThemeColour(theme: Theme) {
  const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#111b21' : '#ffffff');
}

function apply(density: Density, theme: Theme) {
  const root = document.documentElement;
  root.dataset.density = density;
  root.dataset.theme = theme;
  setThemeColour(theme);
}

function stored<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  try {
    const v = localStorage.getItem(key);
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Before the first paint, so a dark-mode user never sees a white flash. */
export function applyStoredDisplay() {
  apply(stored(DENSITY_KEY, 'normal', DENSITIES), stored(THEME_KEY, 'light', THEMES));
}

export function useDisplay() {
  const [density, setDensity] = useStoredChoice<Density>(DENSITY_KEY, 'normal', DENSITIES);
  const [theme, setTheme] = useStoredChoice<Theme>(THEME_KEY, 'light', THEMES);
  useEffect(() => {
    apply(density, theme);
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const follow = () => setThemeColour('system');
    mq.addEventListener('change', follow);
    return () => mq.removeEventListener('change', follow);
  }, [density, theme]);
  return { density, setDensity, theme, setTheme };
}
